import Modal from '@organisms/Modal/Modal';
import { useTranslations } from 'next-intl';
import { dateToShortMonth } from '@helpers/date';
import Icon from '@atoms/Icon/Icon';
import IllustrationIcon from '@icons/Illustration_icon.svg';
import { AxfoodStoreFlyerViewModel } from '@occ/api-client';
import {
  StyledHeading,
  StyledDescription,
  StyledButtonGroup,
  StyledFlyerOption,
  StyledFlyerOptionText,
} from './StoreFlyerModal.styles';
import Text from '@atoms/Text/Text';
import { trackFlyerSelected } from '@helpers/analyticsHelpers/trackPromotionPage';

interface Props {
  data: AxfoodStoreFlyerViewModel;
  onClose: () => void;
}

const StoreFlyerModal = ({ data, onClose }: Props) => {
  const t = useTranslations('promotionPage.storeFlyerModal');
  const hasNextWeekDates = data.displayNextWeekButton && !!data.nextWeekStartDate && !!data.nextWeekEndDate;
  const hasCurrentWeekDates = !!data.currentWeekStartDate && !!data.currentWeekEndDate;

  const handleFlyerClick = (url: string, label: 'current_week_flyer' | 'next_week_flyer') => {
    trackFlyerSelected(label);
    window.open(url, '_blank', 'noreferrer');
    onClose();
  };

  return (
    <Modal size="medium" onClose={onClose} closeOnEscape hasCloseIcon>
      <StyledHeading variant="h2" size="small">
        {data.name}
      </StyledHeading>
      <StyledDescription type="body">{t('description')}</StyledDescription>
      <StyledButtonGroup>
        <StyledFlyerOption type="button" onClick={() => handleFlyerClick(data.currentWeekUrl, 'current_week_flyer')}>
          <Icon svg={IllustrationIcon} size={48} />
          <StyledFlyerOptionText>
            <Text type="subhead">{t('currentWeek')}</Text>
            {hasCurrentWeekDates && (
              <Text type="body" size="small">
                {t('dateSpan', {
                  startDate: dateToShortMonth(data.currentWeekStartDate ?? 0),
                  endDate: dateToShortMonth(data.currentWeekEndDate ?? 0),
                })}
              </Text>
            )}
          </StyledFlyerOptionText>
        </StyledFlyerOption>
        <StyledFlyerOption
          type="button"
          $disabled={!hasNextWeekDates}
          disabled={!hasNextWeekDates}
          onClick={hasNextWeekDates ? () => handleFlyerClick(data.nextWeekUrl, 'next_week_flyer') : undefined}
        >
          <Icon svg={IllustrationIcon} size={48} />
          <StyledFlyerOptionText>
            <Text type="subhead">{t('nextWeek')}</Text>
            {hasNextWeekDates && (
              <Text type="body" size="small">
                {t('dateSpan', {
                  startDate: dateToShortMonth(data.nextWeekStartDate ?? 0),
                  endDate: dateToShortMonth(data.nextWeekEndDate ?? 0),
                })}
              </Text>
            )}
          </StyledFlyerOptionText>
        </StyledFlyerOption>
      </StyledButtonGroup>
    </Modal>
  );
};

export default StoreFlyerModal;
